import { useAuth } from "../../providers/AuthProvider";
import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import noData from "../../assets/svg/undraw_no-data.svg";
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import {Button, CardActions, CardContent} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useNavigate} from "react-router-dom";

interface LpData {
  config: string;
  title?: string;
  createAt: Timestamp;
  hash: string;
  org: string;
}


const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'start',
    color: (theme.vars ?? theme).palette.text.secondary,
    ...theme.applyStyles('dark', {
        backgroundColor: '#1A2027',
    }),
}));

export function ListaLanding() {
  const navigate = useNavigate();
  const { customClaims } = useAuth();

  const [lpItems, setLpItems] = useState<LpData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customClaims?.user_id) return;

    let unsubscribe: (() => void) | undefined;

    (async () => {
      // console.log("async work", {customClaims});
      try {
        setLoading(true);

        // 1️⃣ recupero org dell’utente
        const userRef = doc(db, "users", customClaims.user_id);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          throw new Error("Utente non trovato");
        }

        const orgId = userSnap.data().org;
        if (!orgId) {
          throw new Error("Org non presente sull’utente");
        }

        // 2️⃣ query LP
        const lpQuery  = customClaims["grants"].includes("admin") ? query(
            collection(db, "lp"),
            orderBy("createAt", "desc"),
        ) : query(
          collection(db, "lp"),
          where("org", "==", orgId),
          orderBy("createAt", "desc"),
        );

        // 3️⃣ realtime listener
        unsubscribe = onSnapshot(
          lpQuery,
          (snap) => {
            const items = snap.docs.map((doc) => ({...doc.data(), hash: doc.id}) as LpData);
            setLpItems(items);
            setLoading(false);
          },
          (err) => {
            console.error(err);
            setError("Errore nel caricamento delle landing");
            setLoading(false);
          },
        );
      } catch (err) {
        console.error(err);
        setError("Errore inizializzazione dati");
        setLoading(false);
      }
    })();

    // 4️⃣ cleanup serio
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [customClaims?.user_id]);

  if (loading) return <div className="container">Caricamento…</div>;
  if (error) return <div className="container">{error}</div>;
  return (
    <div className="container">
      {lpItems.length === 0 && (
        <div className={"container"} style={{ marginTop: "1.5em" }}>
          <div style={{ alignItems: "center" }} className={"flex-col"}>
            <img
              style={{ width: "125px", paddingBottom: ".5em" }}
              src={noData}
              alt="delle cartelline vuote"
            />
            <span style={{ fontWeight: "300", fontSize: "14px" }}>
              Nessun dato
            </span>
          </div>
        </div>
      )}
      {lpItems.map((lp) => (
        <Grid key={lp.hash} container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} style={{marginTop: '1.5em'}}>
            <Grid size={6}>
                <Item>
                    <CardContent>
                        <Typography gutterBottom variant="h5" component="div" style={{color: "#333333"}}>
                            {lp.title ?? 'Landing page'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            creata il {lp.createAt.toDate().toLocaleString()}
                        </Typography>
                    </CardContent>
                    <CardActions>
                        <Button size="small" variant={"contained"} onClick={() => navigate(`../lp-standalone/${lp.hash}`)}>Vai</Button>
                    </CardActions>
                </Item>
            </Grid>
        </Grid>
      ))}
    </div>
  );
}
